import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError, API_BASE } from "@/lib/api";
import { Btn, Field, TextInput, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { UploadCloud, ShieldCheck, KeyRound } from "lucide-react";

const empty = { ssh_host: "", ssh_port: 22, ssh_user: "ec2-user", mysql_host: "127.0.0.1", mysql_port: 3306, mysql_user: "", mysql_password: "", mysql_db: "gg_dashboard" };

export default function AdminDbConfigPage() {
  const [cfg, setCfg] = useState(empty);
  const [saved, setSaved] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/db-config").then((r) => { if (r.data) { setSaved(r.data); setCfg({ ...empty, ...r.data, mysql_password: "" }); } });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/db-config", cfg);
      toast.success("Configuration saved");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const uploadPem = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API_BASE}/db-config/pem`, { method: "POST", body: fd, credentials: "include" });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "upload failed");
      toast.success(`Uploaded ${j.filename}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/db-config/test", null, { timeout: 45000 });
      setTestResult(data);
      if (data.ok) toast.success(`Connected · MySQL ${data.version}`);
      else toast.error(data.error || "Connection failed");
    } catch (e) {
      const msg = e.code === "ECONNABORTED" ? "Request timed out after 45s (SSH host likely unreachable — check security group + host)" : fmtError(e);
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    }
    finally { setTesting(false); }
  };

  return (
    <AppShell title="Database Connection" subtitle="Secure jumphost + MySQL configuration. PEM key stays server-side, password is encrypted at rest.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="wv-card p-8">
          <div className="wv-overline">SSH Jumphost</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit" }}>Bastion access</h3>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <Field label="Host"><TextInput data-testid="ssh-host" value={cfg.ssh_host} onChange={(e) => setCfg({ ...cfg, ssh_host: e.target.value })} placeholder="bastion.example.com" className="font-mono"/></Field>
            <Field label="Port"><TextInput data-testid="ssh-port" type="number" value={cfg.ssh_port} onChange={(e) => setCfg({ ...cfg, ssh_port: parseInt(e.target.value) })} className="font-mono"/></Field>
            <div className="col-span-2"><Field label="User"><TextInput data-testid="ssh-user" value={cfg.ssh_user} onChange={(e) => setCfg({ ...cfg, ssh_user: e.target.value })} className="font-mono"/></Field></div>
            <div className="col-span-2">
              <Field label="PEM key">
                <div className="flex items-center gap-3">
                  <label className="wv-btn-secondary h-10 px-4 rounded-xl text-sm font-medium inline-flex items-center gap-2 cursor-pointer">
                    <UploadCloud size={16}/> {uploading ? "Uploading..." : "Upload .pem"}
                    <input data-testid="pem-upload" type="file" accept=".pem,.key" className="hidden" onChange={(e) => uploadPem(e.target.files?.[0])} />
                  </label>
                  {saved?.has_pem ? <Badge tone="success"><KeyRound size={12} className="inline mr-1"/> {saved.pem_filename}</Badge> : <Badge tone="warning">no key uploaded</Badge>}
                </div>
              </Field>
            </div>
          </div>
        </div>

        <div className="wv-card p-8">
          <div className="wv-overline">MySQL Target</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit" }}>Database credentials</h3>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <Field label="Host (internal)"><TextInput value={cfg.mysql_host} onChange={(e) => setCfg({ ...cfg, mysql_host: e.target.value })} className="font-mono"/></Field>
            <Field label="Port"><TextInput type="number" value={cfg.mysql_port} onChange={(e) => setCfg({ ...cfg, mysql_port: parseInt(e.target.value) })} className="font-mono"/></Field>
            <Field label="User"><TextInput data-testid="mysql-user" value={cfg.mysql_user} onChange={(e) => setCfg({ ...cfg, mysql_user: e.target.value })} className="font-mono"/></Field>
            <Field label={saved?.has_password ? "Password (blank = keep)" : "Password"}>
              <TextInput data-testid="mysql-password" type="password" value={cfg.mysql_password} onChange={(e) => setCfg({ ...cfg, mysql_password: e.target.value })} />
            </Field>
            <div className="col-span-2"><Field label="Default database"><TextInput value={cfg.mysql_db || ""} onChange={(e) => setCfg({ ...cfg, mysql_db: e.target.value })} className="font-mono"/></Field></div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Btn data-testid="db-save" onClick={save}>Save configuration</Btn>
        <Btn variant="secondary" data-testid="db-test" onClick={test} disabled={testing}>
          <ShieldCheck size={16} className="inline mr-1"/>{testing ? "Testing... (up to 45s)" : "Test connection"}
        </Btn>
      </div>

      {testResult && (
        <div className={`wv-card p-6 mt-6 ${testResult.ok ? "border-l-4 border-l-[var(--wv-success)]" : "border-l-4 border-l-[var(--wv-error)]"}`} data-testid="db-test-result">
          <div className="flex items-center gap-2 mb-3">
            <Badge tone={testResult.ok ? "success" : "danger"}>
              {testResult.ok ? "SUCCESS" : "FAILED"}
            </Badge>
            {testResult.ok && testResult.version && (
              <span className="text-sm text-[var(--wv-text-2)] font-mono">MySQL {testResult.version} · {testResult.server_time}</span>
            )}
          </div>
          {!testResult.ok && (
            <div>
              <div className="wv-overline mb-2">Error</div>
              <pre className="text-xs font-mono bg-[var(--wv-surface-alt)]/60 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-words">{testResult.error}</pre>
              <div className="mt-3 text-xs text-[var(--wv-text-3)]">
                Common causes: no PEM uploaded · SSH port 22 blocked in your SSH host's Security Group · wrong username · MySQL credentials wrong · MySQL host not reachable from the bastion.
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
